import {
  differenceInYears,
  parseISO,
  isValid,
  getYear,
  addYears,
  subDays,
  format,
  isAfter,
  isBefore,
  addDays,
  addMonths,
  subMonths,
  subYears,
  endOfYear,
  startOfYear,
  differenceInDays,
  startOfDay,
} from "date-fns"

// generate policy expiry date based on start date and include last day flag
export const generatePolicyExpiryDate = (startDate, includeLastDay = 0) => {
  if (!startDate) return null
  const date = typeof startDate === "string" ? parseISO(startDate) : startDate
  const oneYearLater = addYears(date, 1)
  const adjustedDate = subDays(oneYearLater, includeLastDay)

  return format(adjustedDate, "yyyy-MM-dd")
}

// calculate age based on birth date
export const calculateAge = (birthDate) => {
  if (!birthDate) return null
  const parsedDate = parseISO(birthDate)
  return differenceInYears(new Date(), parsedDate)
}

// check if the passed values is a valid date or not
export const isValidDate = (value) => {
  if (!value) return false
  let date
  // Reject numeric timestamps explicitly
  if (typeof value === "number") {
    return false
  }

  // If it's a string
  if (typeof value === "string") {
    // Try parsing as ISO first
    date = parseISO(value)

    // Fallback: try native Date constructor
    if (!isValid(date)) {
      date = new Date(value)
    }
  }
  // If it's already a Date object
  else if (value instanceof Date) {
    date = value
  } else {
    return false
  }

  return isValid(date)
}

// get the year from date
export const getYearFromDate = (date) => {
  if (!date || !isValidDate(date)) return null
  return getYear(date)
}

// formatedd Today Date for Date Picker Input

export const formatedTodayData = () => {
  return format(new Date(), "yyyy-MM-dd")
}

// get the current hours and minutes
export const timeNow = () => {
  return format(new Date(), "hh:mm")
}

// format date
export const formatDate = (
  date,
  {
    is12Hours = false,
    is12HoursWithSeconds = false,
    is12HoursAMPM = false,
    is12HoursAMPMWithSeconds = false,
    is24Hours = false,
    apiFormat = false,
    apiFormatwith12Hours = false,
    apiFormatwith12HoursWithSeconds = false,
    apiFormatwith12HoursAMPM = false,
    apiFormatwith12HoursAMPMWithSeconds = false,
    apiFormatwith24Hours = false,
    apiFormatwith24HoursWithSeconds = false,
    previewFormat = true,
    previewFormatWithMonthAbbr = false,
    previewFormatwith12Hours = false,
    previewFormatwith12HoursWithSeconds = false,
    previewFormatwith12HoursAMPM = false,
    previewFormatwith12HoursAMPMWithSeconds = false,
    previewFormatwith24Hours = false,
  } = {
    previewFormat: true,
  }
) => {
  let desiredFormat = "dd-MM-yyyy"

  if (previewFormat) desiredFormat = "dd-MM-yyyy"
  if (previewFormatWithMonthAbbr) desiredFormat = "dd-MMM-yyyy"
  if (previewFormatwith12Hours) desiredFormat = "dd-MM-yyyy hh:mm"
  if (previewFormatwith12HoursWithSeconds) desiredFormat = "dd-MM-yyyy hh:mm:ss"
  if (previewFormatwith12HoursAMPM) desiredFormat = "dd-MM-yyyy hh:mm a"
  if (previewFormatwith12HoursAMPMWithSeconds)
    desiredFormat = "dd-MM-yyyy hh:mm:ss a"
  if (previewFormatwith24Hours) desiredFormat = "dd-MM-yyyy HH:mm"
  if (apiFormat) desiredFormat = "yyyy-MM-dd"
  if (apiFormatwith12Hours) desiredFormat = "yyyy-MM-dd hh:mm"
  if (apiFormatwith12HoursWithSeconds) desiredFormat = "yyyy-MM-dd hh:mm:ss"
  if (apiFormatwith12HoursAMPM) desiredFormat = "yyyy-MM-dd hh:mm a"
  if (apiFormatwith12HoursAMPMWithSeconds)
    desiredFormat = "yyyy-MM-dd hh:mm:ss a"
  if (apiFormatwith24Hours) desiredFormat = "yyyy-MM-dd HH:mm"
  if (apiFormatwith24HoursWithSeconds) desiredFormat = "yyyy-MM-dd HH:mm:ss"
  if (is12Hours) desiredFormat = "hh:mm"
  if (is12HoursWithSeconds) desiredFormat = "hh:mm:ss"
  if (is12HoursAMPMWithSeconds) desiredFormat = "hh:mm:ss a"
  if (is12HoursAMPM) desiredFormat = "hh:mm a"
  if (is24Hours) desiredFormat = "HH:mm"

  if (!date || !isValidDate(date)) return ""
  return format(date, desiredFormat)
}

// generate disabled dates for date picker
export const generateDisabledDateForDP = (
  date,
  {
    disableFutureDates,
    disableFutureDatesAndToday,
    disablePastDates,
    disablePastDatesAndToday,
  }
) => {
  const today = startOfDay(new Date())
  const d = startOfDay(date) // normalize the incoming date too

  if (disableFutureDates) return isAfter(d, today)
  if (disableFutureDatesAndToday) return !isBefore(d, today)
  if (disablePastDates) return isBefore(d, today)
  if (disablePastDatesAndToday) return !isAfter(d, today)

  return isAfter(d, today) // default: disable future dates
}

/**
 * @typedef {'days' | 'months' | 'years'} DateUnit
 */

/**
 * Adds time to a given date.
 * @param {Date|string} date - The base date
 * @param {number} amount - The number of units to add
 * @param {DateUnit} unit - Unit of time ('days' | 'months' | 'years')
 * @returns {Date}
 */

export const addToDate = (date, amount, unit) => {
  if (!date || !isValidDate(date)) return
  const parsedDate = typeof date === "string" ? new Date(date) : date
  switch (unit) {
    case "days":
      return addDays(parsedDate, amount)
    case "months":
      return addMonths(parsedDate, amount)
    case "years":
      return addYears(parsedDate, amount)
    default:
      return ""
  }
}

/**
 * Subtracts time from a given date.
 * @param {Date|string} date - The base date
 * @param {number} amount - The number of units to subtract
 * @param {DateUnit} unit - Unit of time ('days' | 'months' | 'years')
 * @returns {Date}
 */

export const subtractFromDate = (date, amount, unit) => {
  if (!date || !isValidDate(date)) return

  const parsedDate = typeof date === "string" ? new Date(date) : date
  switch (unit) {
    case "days":
      return subDays(parsedDate, amount)
    case "months":
      return subMonths(parsedDate, amount)
    case "years":
      return subYears(parsedDate, amount)
    default:
      return ""
  }
}

/**
 * get the end of the year from a given date.
 * @param {Date|string} date - The base date
 * @returns {Date}
 */

export const getEndOfYearFromDate = (date) => {
  if (!date || !isValidDate(date)) return

  const parsedDate = typeof date === "string" ? new Date(date) : date
  return endOfYear(parsedDate)
}

/**
 * get the start of the year from a given date.
 * @param {Date|string} date - The base date'}
 * @returns {Date}
 */

export const getStartOfYearFromDate = (date) => {
  if (!date || !isValidDate(date)) return

  const parsedDate = typeof date === "string" ? new Date(date) : date
  return startOfYear(parsedDate)
}

/**
 * get the difference in days from two dates
 * @param {Date|string} baseDate - The base date
 * @param {Date|string} date - The date you want to subtract from
 * @returns {number}
 */

//get the difference in days from two dates
export const getDayDiffFromDate = (baseDate, date) => {
  if (!baseDate || !date) return null
  const parsedBaseDate =
    typeof baseDate === "string" ? parseISO(baseDate) : baseDate
  const parsedDate = typeof date === "string" ? parseISO(date) : date
  return differenceInDays(parsedBaseDate, parsedDate)
}

// generate drop down options of years
// from current year back to passed length - 1
// note : the current will be calculated into the length
export const generateYearsOptions = (length) => {
  const arrayLength = length
  const currentDate = new Date()

  // check if the current month is december
  const isTheCurrentMonthIsDecember = format(currentDate, "MM") === "12"
  // next year option
  const nextYearOption = format(addYears(currentDate, 1), "yyyy")

  const years = Array.from({ length: arrayLength }).map((_, i) => {
    const yearOption = format(subYears(currentDate, i), "yyyy")
    return {
      value: `${yearOption}`,
      label: `${yearOption}`,
    }
  })

  // add the next year option if the current month is december
  const yearsOptions = isTheCurrentMonthIsDecember
    ? [{ value: `${nextYearOption}`, label: `${nextYearOption}` }, ...years]
    : years

  return yearsOptions
}
